export interface EnglishLessonContent {
  id: string;
  title: string;
  vietnameseTitle: string;
  lop: number;
  chuong: number;
  bai: number;
  description: string;
  difficulty: string;
  estimatedTime: number;
  vocabulary: Array<{
    id: string;
    word: string;
    phonetic: string;
    meaning: string;
    example: string;
    exampleTranslation: string;
  }>;
  sentences: Array<{
    id: string;
    text: string;
    translation: string;
  }>;
  paragraphs: Array<{
    id: string;
    text: string;
    translation: string;
  }>;
}

export interface EnglishLessonContent {
  id: string;
  title: string;
  vietnameseTitle: string;
  lop: number;
  chuong: number;
  bai: number;
  description: string;
  difficulty: string;
  estimatedTime: number;
  vocabulary: Array<{
    id: string;
    word: string;
    phonetic: string;
    meaning: string;
    example: string;
    exampleTranslation: string;
  }>;
  sentences: Array<{
    id: string;
    text: string;
    translation: string;
  }>;
  paragraphs: Array<{
    id: string;
    text: string;
    translation: string;
  }>;
}

class EnglishContentService {
  private lessonsCache: EnglishLessonContent[] = [];
  private isInitialized = false;
  private initPromise: Promise<void> | null = null;

  // Initialize and load all lessons immediately
  async initialize(): Promise<void> {
    if (this.isInitialized) return;
    if (this.initPromise) return this.initPromise;

    this.initPromise = (async () => {
      try {
        console.log('📚 Starting English content initialization...');
        const startTime = performance.now();

        // Use Vite's import.meta.glob to find all english.json files
        // Eager load (eager: true) to get data immediately
        const modules = import.meta.glob('../../du-lieu/english/lop-*/chuong-*/bai-*/english.json', {
          eager: true,
          import: 'default'
        });

        const lessons: EnglishLessonContent[] = [];

        for (const path in modules) {
          try {
            const lessonData = modules[path] as any;

            // Extract metadata from path if needed, or rely on content
            // Path format: ../../du-lieu/english/lop-10/chuong-1/bai-1/english.json
            const match = path.match(/lop-(\d+)\/chuong-(\d+)\/bai-(\d+)/);

            if (match && lessonData) {
              const [_, lop, chuong, bai] = match;

              // Ensure metadata matches path
              const lesson: EnglishLessonContent = {
                ...lessonData,
                lop: parseInt(lop),
                chuong: parseInt(chuong),
                bai: parseInt(bai)
              };

              lessons.push(lesson);
            }
          } catch (err) {
            console.warn(`Failed to parse lesson at ${path}`, err);
          }
        }

        this.lessonsCache = lessons;
        this.isInitialized = true;

        // Cache to localStorage for even faster subsequent loads (optional)
        try {
          localStorage.setItem('english_lessons_cache_timestamp', Date.now().toString());
          // We might not want to store the whole content in localStorage if it's too large
          // But we can store a flag saying we've indexed them
        } catch (e) {
          console.warn('Failed to cache to localStorage', e);
        }

        const endTime = performance.now();
        console.log(`✅ English content initialized: ${lessons.length} lessons in ${(endTime - startTime).toFixed(2)}ms`);
      } catch (error) {
        console.error('❌ Failed to initialize English content:', error);
        throw error;
      } finally {
        this.initPromise = null;
      }
    })();

    return this.initPromise;
  }

  async getAllLessons(): Promise<EnglishLessonContent[]> {
    if (!this.isInitialized) {
      await this.initialize();
    }
    return this.lessonsCache;
  }

  async getLesson(lop: number, chuong: number, bai: number): Promise<EnglishLessonContent | undefined> {
    if (!this.isInitialized) {
      await this.initialize();
    }
    return this.lessonsCache.find(l => l.lop === lop && l.chuong === chuong && l.bai === bai);
  }

  // Legacy method support (but optimized)
  async loadEnglishLesson(lop: number, chuong: number, bai: number): Promise<EnglishLessonContent> {
    const lesson = await this.getLesson(lop, chuong, bai);
    if (!lesson) {
      throw new Error(`Lesson not found: Lớp ${lop} - Chương ${chuong} - Bài ${bai}`);
    }
    return lesson;
  }
}

export const englishContentService = new EnglishContentService();
